import { DeleteTwoTone, EditTwoTone } from "@ant-design/icons";
import { Dropdown, Menu, message, Popconfirm, Typography } from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import {
  EXPORT_SHIPMENT,
  IMPORT_VEHICLE,
} from "../../../apiServices/API_ENDPOINTS";
import { deleteData, getData } from "../../../apiServices/common";
import ListTable from "common/ListTable";
import AppContent from "../../../common/AppContent";
import AppPageHeader from "../../../common/PageHeader";
import {
  EXPORT_SHIPMENT_CREATE_PERMISSION,
  EXPORT_SHIPMENT_DELETE_PERMISSION,
  EXPORT_SHIPMENT_EDIT_PERMISSION,
  GOODS_DELIVERY_CREATE_PERMISSION,
  GOODS_DELIVERY_DELETE_PERMISSION,
  GOODS_DELIVERY_EDIT_PERMISSION,
} from "../../../routes/permissions";
import { cglFormatDate, hasPermission } from "../../../utils/functions";
import CustomFilter from "../../Common/CustomFilter";
import ExtraContent from "../../Configuration/CommonComponents/ExtraContent";

const VehicleStatus = () => {
  // Router
  const location = useLocation();
  const routerPath = location ? location.pathname : null;
  const isExport = routerPath?.startsWith("/export/");
  const shipmentLabel = isExport ? "Export" : "Import";
  const basePath = isExport ? "/export" : "/import";
  const apiEndpoint = isExport ? EXPORT_SHIPMENT : IMPORT_VEHICLE;
  const createPermission = isExport
    ? EXPORT_SHIPMENT_CREATE_PERMISSION
    : GOODS_DELIVERY_CREATE_PERMISSION;
  const editPermission = isExport
    ? EXPORT_SHIPMENT_EDIT_PERMISSION
    : GOODS_DELIVERY_EDIT_PERMISSION;
  const deletePermission = isExport
    ? EXPORT_SHIPMENT_DELETE_PERMISSION
    : GOODS_DELIVERY_DELETE_PERMISSION;

  // States
  const [showFilter, setShowFilter] = useState(
    localStorage.getItem("filter_status") && routerPath
      ? JSON.parse(localStorage.getItem("filter_status"))[routerPath]
      : false,
  );
  const [refresh, setRefresh] = useState(0);
  const [tableData, setTableData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [metaData, setMetaData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  // Antd
  const { Title } = Typography;

  // Router
  const navigate = useNavigate();

  // Filter open/close status update in localStorage
  useEffect(() => {
    const filterStatus = localStorage.getItem("filter_status");
    const parsedFilterStatus = filterStatus ? JSON.parse(filterStatus) : {};

    if (showFilter) {
      parsedFilterStatus[routerPath] = true;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    } else {
      parsedFilterStatus[routerPath] = false;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    }
  }, [showFilter, routerPath, location]);

  const getAllVehicleStatus = useCallback(
    async (filterValues) => {
      setLoading(true);
      setError(null);

      const query = `${apiEndpoint}?page=${currentPage}`;

      const response = await getData(query, false, filterValues);

      if (response && response?.data?.code === 200) {
        setLoading(false);
        setMetaData({
          defaultPageSize: response?.data?.data?.per_page,
          current: response?.data?.data?.current_page,
          defaultCurrent: response?.data?.data?.current_page,
          total: response?.data?.data?.total,
        });
        setTableData(response?.data?.data?.data);
      } else {
        setLoading(false);
        message.error(`Error getting ${shipmentLabel} Shipment for Single Vehicle data`);
      }
    },
    [currentPage, apiEndpoint, shipmentLabel],
  );

  // Effects
  useEffect(() => {
    getAllVehicleStatus();
  }, [refresh, getAllVehicleStatus]);

  const refetch = () => {
    setRefresh(refresh + 1);
  };

  const deleteSelectedVehicle = async (id) => {
    setLoading(true);
    const response = await deleteData(`${apiEndpoint}/${id}`);

    if (response && response?.code === 200) {
      setLoading(false);
      message.success(`${shipmentLabel} Shipment for Single Vehicle Successfully`);
      refetch();
    } else {
      setLoading(false);
      message.error(`Error deleting ${shipmentLabel} Shipment for Single Vehicle`);
    }
  };

  const changePage = (newPage) => {
    setCurrentPage(newPage);
    refetch();
  };

  const tableColumns = [
    {
      title: "Vehicle Number",
      dataIndex: ["vehicle_info", "vehicle_number"],
      key: "vehicle_number",
    },
    ...(isExport
      ? [
          {
            title: "Export Carton",
            dataIndex: ["export_carton"],
            key: "export_carton",
          },
          {
            title: "Garment Piece Quantity",
            dataIndex: ["garment_piece_qty"],
            key: "garment_piece_qty",
          },
        ]
      : [
          {
            title: "LC Number",
            dataIndex: ["b2b_lc_info", "b2b_lc_number"],
            key: "b2b_lc_number",
          },
          {
            title: "Loading Quantity",
            dataIndex: ["loading_qty"],
            key: "loading_qty",
          },
        ]),
    {
      title: "Challan No",
      dataIndex: ["vehicle_info", "challan_info", "challan_no"],
      key: "challan_no",
    },
    {
      title: "Bolt Seal No",
      dataIndex: ["vehicle_info", "security_bolt_seal_number"],
      key: "security_bolt_seal_number",
    },
    {
      title: "Loading Point",
      dataIndex: ["vehicle_info", "loading_place"],
      key: "loading_place",
    },
    {
      title: "Dispatch Date",
      dataIndex: ["vehicle_info", "dispatch_time"],
      key: "dispatch_time",
      render: (value, record) => {
        return <span>{value ? cglFormatDate(value) : "N/A"}</span>;
      },
    },
    {
      title: "Action",
      key: "action",
      width: "10%",
      render: (_, record) => {
        return (
          <Dropdown.Button
            overlay={
              <Menu
                items={[
                  {
                    label: (
                      <Popconfirm
                        title="Are you sure you want to delete?"
                        onConfirm={() => {
                          deleteSelectedVehicle(record?.vehicle_info?.id);
                        }}
                      >
                        <span>
                          <DeleteTwoTone />
                        </span>
                        <span
                          style={{
                            color: "red",
                            marginLeft: 5,
                          }}
                        >
                          Delete
                        </span>
                      </Popconfirm>
                    ),
                    key: "1",
                    disabled: !hasPermission([deletePermission]),
                  },
                  {
                    label: (
                      <>
                        <span>
                          <EditTwoTone />
                        </span>
                        <span
                          type="button"
                          style={{ marginLeft: 5 }}
                          onClick={() => {
                            navigate(
                              `${basePath}/edit-goods-delivery/${record?.vehicle_info?.id}`,
                            );
                          }}
                        >
                          Edit
                        </span>
                      </>
                    ),
                    key: "2",
                    disabled: !hasPermission([editPermission]),
                  },
                ]}
              />
            }
          />
        );
      },
    },
  ];

  const memoizedCustomFilter = useMemo(
    () => (
      <CustomFilter
        showFilter={showFilter}
        tableData={tableData}
        fetchTableData={getAllVehicleStatus}
        filterName="import_vehicle_status"
      />
    ),
    [showFilter, tableData, getAllVehicleStatus],
  );

  return (
    <>
      <AppContent
        header={
          <AppPageHeader
            extra={
              <ExtraContent
                refresh={refresh}
                setRefresh={setRefresh}
                showFilter={showFilter}
                setShowFilter={setShowFilter}
                addButtonText={
                  hasPermission([createPermission])
                    ? "Add New"
                    : "No Permission"
                }
                disabled={!hasPermission([createPermission])}
                handleAddItemOpen={() => navigate(`${basePath}/add-goods-delivery`)}
              />
            }
            title={<Title level={5}>{shipmentLabel} Shipment for Single Vehicle</Title>}
          />
        }
        content={
          <>
            {memoizedCustomFilter}

            <div className="table" style={{ marginTop: 10 }}>
              <ListTable
                loading={loading}
                error={error}
                onRetry={() => getAllVehicleStatus()}
                emptyText="No records to show yet."
                dataSource={tableData}
                columns={tableColumns}
                bordered
                pagination={{
                  showSizeChanger: false,
                  defaultPageSize: metaData?.defaultPageSize,
                  current: metaData?.current,
                  onChange: (newPage) => {
                    changePage(newPage);
                  },
                  defaultCurrent: metaData?.defaultCurrent,
                  total: metaData?.total,
                }}
              />
            </div>
          </>
        }
      />
    </>
  );
};

export default VehicleStatus;
